import { CardImage, Integration, PaymentOption } from '../../model'
import type { Money } from '@afterpay/types'
import {
  useApplePayEntryData,
  useCheckoutToken,
  useConsumerSelector,
  useCheckoutState,
  useCohort,
  usePaymentOptions,
  useSelectedPaymentOption,
  useOfferDetails,
  authLogout,
  closeCheckout,
  persistor,
  useAppDispatch,
  useAuthentication,
  useRedirectUrl,
  useCreateCheckoutMutation,
  CREATE_CHECKOUT_FIXED_KEY,
  useSelectedPaymentType,
} from '../../state'
import { buildSaveOfferParams, buildIssueCardParams } from './utils'
import type { AppleGatewaySaveOfferRequest, AppleGatewayIssueCardRequest, UserCohort } from '../../state/appleGateway/apis'
import { CheckoutPaymentSchedule, Cohort, OfferDetail } from '@afterpaytouch/portal-api/types'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { app } from '../../env'
import { TrackingEvent } from '../../model/monitoring'
import { useMerchantCallback } from '../../utils/merchant'
import { getCurrentUserContextFromClient, useLDClient } from '@afterpaytouch/feature'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { useTransactionMessage } from '../../utils/transactionMessage'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { stopDatadogSession } from '@afterpaytouch/integrations/datadog'
import { useRef } from 'react'

/**
 * OfferDetail enriched with interestAmount and paymentSchedules from PaymentOption
 */
export interface EnrichedOfferDetail extends OfferDetail {
  interestAmount?: Money
  paymentSchedules: CheckoutPaymentSchedule[]
}

export { useFinaliseApplePaylaterCheckout } from './hooks/useFinaliseApplePaylaterCheckout'

export const useAddedCardType = (): Integration | undefined => {
  const applePayEntryData = useApplePayEntryData()
  const addedCardConditions = [{ condition: applePayEntryData?.isValid, value: Integration.ApplePay }]

  const addedCardType = addedCardConditions.find(({ condition }) => condition)?.value
  return addedCardType
}

export const useCardImage = (): string => {
  const addedCardType = useAddedCardType()
  if (addedCardType === undefined) {
    return ''
  }

  return CardImage[addedCardType]
}

// Map Cohort to UserCohort for API
const getUserCohort = (cohort: Cohort | undefined): UserCohort => {
  return cohort === Cohort.CashOnly ? 'CASHAPP' : 'AFTERPAY'
}

// Map PaymentOption to EnrichedOfferDetail for API
const mapPaymentOptionToOffer = (paymentOption: PaymentOption): EnrichedOfferDetail => ({
  monthlyAmount: paymentOption.installmentAmount,
  amountDueToday: paymentOption.amountDueToday,
  totalAmount: paymentOption.totalAmount,
  numberOfInstallments: paymentOption.installmentsCount,
  apr: paymentOption.apr?.toString(),
  paymentType: paymentOption.paymentType,
  interestAmount: paymentOption.interestAmount ?? undefined,
  paymentSchedules: paymentOption.paymentSchedules ?? [],
})

// Map OfferDetail from checkout/start to EnrichedOfferDetail format
const mapOfferDetailToEnriched = (offer: OfferDetail, paymentSchedules?: CheckoutPaymentSchedule[]): EnrichedOfferDetail => ({
  monthlyAmount: offer.monthlyAmount,
  amountDueToday: offer.amountDueToday,
  totalAmount: offer.totalAmount,
  numberOfInstallments: offer.numberOfInstallments,
  apr: offer.apr,
  paymentType: offer.paymentType,
  paymentSchedules: paymentSchedules ?? [],
})

export const useSaveOfferParams = (): AppleGatewaySaveOfferRequest | null => {
  const token = useCheckoutToken()
  const consumer = useConsumerSelector()
  const { started } = useCheckoutState()
  const cohort = useCohort()
  const paymentOptions = usePaymentOptions()
  const selectedPaymentOption = useSelectedPaymentOption()
  const offerDetails = useOfferDetails()
  const [, { data: createCheckoutData }] = useCreateCheckoutMutation({ fixedCacheKey: CREATE_CHECKOUT_FIXED_KEY })

  const userCohort = getUserCohort(cohort)
  const expires = createCheckoutData?.expires
  const selectedPaymentType = useSelectedPaymentType()
  const hasPaymentOptions = paymentOptions.length > 0

  // When payment options exist, each offer has its own paymentSchedules
  // When falling back to offerDetails, use started.paymentSchedules for all offers
  const allOffers = hasPaymentOptions
    ? paymentOptions.map(mapPaymentOptionToOffer)
    : offerDetails.map((offer) => mapOfferDetailToEnriched(offer, started?.paymentSchedules))

  let selectedOffer: EnrichedOfferDetail | undefined

  if (hasPaymentOptions && valueIsNotNullOrUndefined(selectedPaymentOption)) {
    selectedOffer = mapPaymentOptionToOffer(selectedPaymentOption)
  } else if (!hasPaymentOptions && allOffers.length > 0) {
    if (selectedPaymentType !== null) {
      selectedOffer = allOffers.find((offer) => offer.paymentType === selectedPaymentType)
    }
    // Fallback to first offer if selectedPaymentType is not found
    if (selectedOffer === undefined) {
      selectedOffer = allOffers[0]
    }
  }

  return buildSaveOfferParams(token, consumer, allOffers, selectedOffer, userCohort, expires)
}

export const useIssueCardParams = (): AppleGatewayIssueCardRequest | null => {
  const token = useCheckoutToken()
  const consumer = useConsumerSelector()

  return buildIssueCardParams(token, consumer)
}

/**
 * Apple Pay Later specific close checkout hook
 *
 * This hook is specifically designed for Apple Pay Later flow.
 * It receives the redirect URL directly from SaveOffer response and performs redirect.
 * - If fetchedRedirectUrl is provided: redirects directly to that URL
 * - Otherwise: falls back to merchant callback URL with query args appended
 *
 * Only used in Apple Pay Later confirmation flow.
 */
export const useCloseApplePaylater: () => (fetchedRedirectUrl: string | undefined, queryArgs: string) => Promise<void> = () => {
  const { initial, checkout, confirmed, referrer } = useCheckoutState()
  const { endTransaction } = useMerchantCallback()
  const { authenticated, persistent } = useAuthentication()
  const dispatch = useAppDispatch()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const redirectUrl = useRedirectUrl()
  const LDClient = useLDClient()
  const closeCount = useRef(1)
  const endTransactionMessage = useTransactionMessage()

  const reportError = (message: string, error): void => {
    logEvent(TrackingEvent.CHECKOUT_FATAL_ERROR, {
      error: message,
      merchantName: checkout?.merchant.name,
      merchantId: checkout?.merchant.id,
    })

    const context = {
      'Merchant Data': { ...checkout?.merchant },
      'Order confirmed?': { confirmed: confirmed !== undefined, ...(confirmed ?? {}) },
      'Error details': { ...error },
    }

    capture.error(error, context)
  }

  const clearSession = async (): Promise<void> => {
    dispatch(closeCheckout())
    if (persistor === null) {
      return
    }
    await persistor.flush()
  }

  const getCallbackUrl = (): string => {
    if (redirectUrl.length === 0) {
      reportError('No baseCallbackUrl to return to', undefined)
      return referrer ?? app.BROCHURE_BASE_URL
    }
    const callbackUrl = redirectUrl + (initial?.relativeCallbackUrl ?? '')
    return callbackUrl
  }

  const trackCloseCount = (): void => {
    logEvent(TrackingEvent.CLOSE_CALLED, {
      closeCount: closeCount.current,
    })
    closeCount.current += 1
  }

  return async (fetchedRedirectUrl: string | undefined, queryArgs: string) => {
    const logout = async (): Promise<void> => {
      if (authenticated && !persistent) {
        await dispatch(authLogout())
      }
    }

    const redirect = async (): Promise<void> => {
      trackCloseCount()

      if (fetchedRedirectUrl !== undefined && fetchedRedirectUrl.length > 0) {
        window.location.href = fetchedRedirectUrl
      } else {
        const callbackUrl = getCallbackUrl()
        const fullUrl = callbackUrl + (queryArgs.length > 0 ? (callbackUrl.includes('?') ? '&' : '?') + queryArgs : '')
        window.location.href = fullUrl
      }
    }

    const closePopup = async (): Promise<void> => {
      try {
        await endTransaction(endTransactionMessage)
      } catch (e) {
        reportError('No confirmation of end transaction', e)
      }
      trackCloseCount()
      await clearSession()
      if (!initial.isIframe) {
        // If we are using zoid then afterpay.js is responsible for closing the iframe
        window.close()
      }
    }

    const key = getCurrentUserContextFromClient(LDClient)?.key ?? null
    if (LDClient !== undefined && key !== null) {
      await LDClient.close()
    }

    await logout()

    stopDatadogSession()

    if (initial.isWindowed || initial.isIframe) {
      await closePopup()
    } else {
      await redirect()
    }
  }
}
